'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'

export default function Post16Content() {
  return (
    <BlogPostLayout
      title="EU Microplastics Ban 2026: What It Means for Consumers"
      author="MicroPlastics Team"
      date="March 1, 2026"
      readTime="8 min read"
      heroImage="/blog/app-home.png"
      heroImageAlt="MicroPlastics app showing product compliance"
    >
      <BlogTLDR>
        <strong>Updated for 2026:</strong> Regulation (EU) 2026/1168 has now
        amended REACH Annex XVII Entry 78, and a 31 May 2026 ECHA reporting
        deadline has taken effect. The EU&rsquo;s microplastic restriction (Regulation
        EU 2023/2055) took
        effect <strong>October 17, 2023</strong>, banning intentionally added
        microplastic particles in products sold in the EU, with{' '}
        <strong>multi-year phase-outs through 2035.</strong> Glitter,
        microbeads in rinse-off cosmetics, and certain detergent capsules
        are already affected. Leave-on cosmetics, fragrance encapsulates,
        and synthetic sports-pitch infill follow on staggered deadlines. The
        US has no equivalent federal restriction.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>EU Regulation 2023/2055 took effect October 17, 2023.</>,
          <>Immediate ban: loose glitter, rinse-off cosmetic microbeads, certain agricultural products.</>,
          <>Phased through 2027-2035: leave-on cosmetics, detergent capsules, fragrance encapsulates, sports-pitch infill.</>,
          <>Estimated 500,000 tonnes of intentionally added microplastic avoided over 20 years.</>,
          <>US still has no equivalent federal regulation; California and a few other states have partial bans.</>,
          <>2026 update: Regulation (EU) 2026/1168 amended REACH Annex XVII Entry 78, clarifying derogations for medicinal products, PPORD, and polymers permanently bound into a solid matrix.</>,
          <>A <strong>31 May 2026</strong> ECHA reporting deadline now requires detailed emission estimates and microplastic identity data from affected companies.</>,
          <>The threshold that triggers the ban is <strong>0.01% by weight</strong> of intentionally added synthetic polymer microparticles.</>,
        ]}
      />

      {/* Introduction */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Most Ambitious Microplastics Regulation in History
      </h2>
      <p>
        In October 2023, the European Union adopted what is widely regarded as the most comprehensive
        microplastics regulation ever enacted. Commission Regulation (EU) 2023/2055, built on years of
        scientific assessment by the European Chemicals Agency (ECHA), places a sweeping ban on
        intentionally added microplastics across a wide range of consumer and industrial products. The
        regulation did not happen overnight. It is the culmination of a restriction proposal that ECHA
        first submitted in 2019, drawing on more than a decade of mounting evidence about the environmental
        and health impacts of synthetic microparticles.
      </p>
      <p>
        For consumers, this regulation represents a turning point. For the first time, a major regulatory
        body has acknowledged that microplastics intentionally added to everyday products -- from face
        scrubs and laundry detergents to artificial turf and agricultural coatings -- pose a risk
        significant enough to warrant a legally binding ban. But as with any complex regulation, the
        details matter enormously. The ban does not cover everything, it does not take full effect
        immediately, and it leaves several notable gaps that consumers need to understand.
      </p>
      <p>
        This guide breaks down exactly what the EU microplastics ban covers, what it does not, how the
        phase-in timeline works, and what it all means for you as someone who buys and uses consumer
        products every day.
      </p>

      {/* ECHA Regulation Background */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 2026 Amendment: What Actually Changed This Year
      </h2>
      <p>
        The 2023 restriction was the headline, but the commercially significant
        deadlines land in 2026 through 2029 -- and 2026 brought the first substantive
        amendment. In 2026 the European Commission published{' '}
        <strong>Commission Regulation (EU) 2026/1168</strong>, amending REACH Annex XVII
        Entry 78. It does not widen the ban. It clarifies the boundaries of three
        derogations that industry had been arguing over: medicinal products, Product
        and Process Orientated Research and Development (PPORD), and synthetic polymer
        microparticles that are <strong>permanently incorporated into a solid matrix</strong>.
      </p>
      <p>
        That last carve-out is the one worth understanding as a consumer, because it
        explains why the ban has not made plastic disappear from shelves. A microplastic
        locked into a solid product that does not shed it during normal use is treated
        differently from loose glitter or a rinse-off scrub. The restriction targets{' '}
        <em>intentionally added, releasable</em> particles, not all polymer content.
      </p>
      <p>
        The concentration threshold is worth stating plainly: the general prohibition
        bites at <strong>0.01% by weight</strong> or above of intentionally added
        synthetic polymer microparticles, whether sold on their own or in a mixture.
      </p>
      <p>
        Alongside the amendment, <strong>31 May 2026</strong> introduced granular ECHA
        reporting obligations. Affected companies must now submit emission estimates and
        microplastic identity data rather than simply reformulating quietly. Rinse-off
        cosmetics face their compliance deadline in <strong>2027</strong>. The practical
        effect for shoppers is that the reformulation wave is still rolling: a product
        legally on sale in 2026 may still contain material that becomes non-compliant
        within the year.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The ECHA Regulation: How We Got Here
      </h2>
      <p>
        The European Chemicals Agency (ECHA) is the EU body responsible for evaluating chemical risks
        and proposing restrictions under the REACH regulation (Registration, Evaluation, Authorisation
        and Restriction of Chemicals). ECHA began investigating intentionally added microplastics in
        2017, responding to growing scientific consensus that these particles were accumulating in
        oceans, soils, freshwater systems, and ultimately in human tissues.
      </p>
      <p>
        In January 2019, ECHA formally proposed a broad restriction on microplastics that are
        intentionally added to products. The proposal defined microplastics as synthetic polymer
        particles smaller than 5 millimeters that are organic, insoluble, and resistant to degradation.
        This definition was carefully crafted to target the particles most likely to persist in the
        environment and cause harm, while excluding naturally occurring polymers and those that
        biodegrade readily.
      </p>
      <p>
        The proposal went through years of public consultation, scientific review, and political
        negotiation. Industry groups pushed back hard, arguing that transition periods were too short
        and that alternatives for some applications did not yet exist. Environmental organizations
        countered that the regulation did not go far enough, particularly in addressing microplastics
        generated through wear and degradation of larger plastic products.
      </p>
      <p>
        After extensive deliberation, the European Commission adopted the restriction on September 25,
        2023, with publication in the Official Journal of the EU on October 17, 2023. The regulation
        entered into force on October 17, 2023, with immediate bans on some product categories and
        extended transition periods for others stretching as far as 2035.
      </p>

      {/* What Is Banned */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What Exactly Is Banned
      </h2>
      <p>
        The regulation targets <strong>intentionally added microplastics</strong> -- synthetic polymer
        particles that manufacturers deliberately include in products for a functional purpose. This
        is a critical distinction. The ban does not address microplastics that form unintentionally
        through degradation of plastic packaging, tire wear, or textile shedding. It focuses
        specifically on particles that are added on purpose during manufacturing.
      </p>
      <p>
        The key product categories affected include:
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Cosmetics and Personal Care Products</h3>
      <p>
        This is the category with the most immediate impact. Microbeads in rinse-off cosmetics (such
        as exfoliating scrubs, shower gels, and toothpaste) were among the first products banned, with
        the restriction taking effect almost immediately after the regulation entered into force.
        Leave-on cosmetics -- products like foundations, primers, moisturizers, lipsticks, and
        sunscreens that stay on the skin -- have longer transition periods because reformulation is
        more technically complex. For a detailed look at which cosmetic ingredients are microplastics,
        read our guide on{' '}
        <Link href="/blog/microplastics-in-cosmetics" className="text-[#1E6B47] hover:underline font-medium">
          microplastics in cosmetics
        </Link>.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Detergents and Cleaning Products</h3>
      <p>
        Synthetic polymer microparticles used in laundry detergents, fabric softeners, dishwasher
        tablets, and surface cleaners as encapsulation agents for fragrances, opacifiers, or
        abrasives fall under the ban. These products have transition periods of approximately four
        to eight years depending on the specific application, giving manufacturers time to develop
        and validate alternative formulations.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Sports Pitches and Artificial Turf</h3>
      <p>
        One of the most discussed aspects of the regulation is the ban on rubber granulate infill
        used in artificial sports pitches. These crumb rubber particles, often made from recycled
        tires, are a massive source of microplastic pollution. Studies have estimated that synthetic
        turf pitches across the EU release approximately 16,000 tonnes of microplastics into the
        environment every year. The regulation includes an eight-year transition period for this
        category, giving sports facilities time to switch to alternative infill materials such as
        cork, coconut fiber, or sand.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Agricultural Products</h3>
      <p>
        Controlled-release fertilizer coatings, seed coatings, and plant protection products that
        use synthetic polymer shells to regulate release timing are covered by the ban. Agriculture
        has some of the longest transition periods (up to 2031 for some applications) because
        finding effective alternatives that maintain crop yields is technically demanding.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Other Industrial and Consumer Products</h3>
      <p>
        The regulation also covers microplastics in paints, coatings, construction materials,
        medical devices (with specific exemptions), and various industrial applications. Glitter,
        which is essentially decorative microplastic, is also banned under the regulation, though
        it has a transition period to allow the craft and cosmetics industries to shift to
        biodegradable alternatives.
      </p>

      {/* Phase-In Timeline */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Phase-In Timeline: 2023 to 2035
      </h2>
      <p>
        The EU did not flip a single switch. Instead, the regulation uses a staggered phase-in
        approach, recognizing that different industries need different amounts of time to
        reformulate products and adopt alternatives. Here is the general timeline:
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>October 2023 (immediate):</strong> Loose microplastic glitter and microbeads sold
          as raw materials were banned from sale immediately upon the regulation entering into force.
        </li>
        <li>
          <strong>October 2027 (4 years):</strong> Rinse-off cosmetics and personal care products
          containing intentionally added microplastics must be reformulated or withdrawn. Detergents
          and maintenance products with microplastic encapsulation also face deadlines in this window.
        </li>
        <li>
          <strong>October 2029 (6 years):</strong> Leave-on cosmetics -- moisturisers, serums,
          sunscreens -- must comply.
        </li>
        <li>
          <strong>October 2031 (8 years):</strong> Sports pitch infill granules must be replaced or
          contained. Agricultural coatings and controlled-release products also face deadlines in
          this period.
        </li>
        <li>
          <strong>October 2035 (12 years):</strong> Lip products, nail products, and make-up get the
          longest transition period in the entire restriction, alongside the most technically
          challenging industrial and medical applications. This reflects the difficulty of
          reformulating products where the synthetic polymer is structural rather than an additive --
          in nail polish the acrylate copolymer <em>is</em> the film. From{' '}
          <strong>17 October 2031</strong> until the cut-off, these colour cosmetics may only remain
          on sale if they carry the statement &quot;This product contains microplastics.&quot; See{' '}
          <Link href="/blog/do-gel-and-acrylic-nails-shed-microplastics" className="text-forest underline">
            what that means for gel and acrylic nails
          </Link>.
        </li>
      </ul>
      <p>
        During each transition period, manufacturers must also comply with reporting and labeling
        requirements, disclosing the presence and quantity of microplastics in their products even
        before the ban takes full effect for that category.
      </p>

      {/* Which Products Are Affected First */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Which Products Are Affected First
      </h2>
      <p>
        If you are shopping today and wondering which products have already changed, the answer
        depends on the category. Loose glitter sold as a standalone product has already been pulled
        from EU shelves. Rinse-off cosmetic products containing microbeads -- exfoliating scrubs,
        peel-off masks, and abrasive toothpastes -- are in the process of being reformulated or
        withdrawn. Many major cosmetic brands, including L&apos;Oreal, Unilever, and Beiersdorf,
        began voluntarily removing microbeads from rinse-off products years before the regulation,
        so the practical impact on store shelves has been somewhat gradual.
      </p>
      <p>
        The bigger changes are still coming. Leave-on cosmetics represent a far larger product
        category, and many contain synthetic polymers that are fundamental to their formulation.
        When the 2029 deadline hits, consumers will see significant reformulations across
        sunscreens, moisturizers, and serums -- while colour cosmetics such as foundation, lipstick
        and nail polish run on a separate, longer clock to 2035. Some products may change texture,
        performance, or appearance as manufacturers replace synthetic polymers with natural
        alternatives. This is worth knowing so you are not surprised when your favorite product
        formula changes.
      </p>

      {/* Exceptions and Loopholes */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Exceptions and Loopholes
      </h2>
      <p>
        No regulation is airtight, and the EU microplastics ban has several notable exceptions
        that consumers should be aware of:
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Polymers that are soluble or biodegradable:</strong> The regulation exempts
          synthetic polymers that dissolve in water or biodegrade under natural conditions within
          a specific timeframe. However, the criteria for &quot;biodegradable&quot; are debated,
          and some polymers classified as biodegradable may still persist in certain environments
          for years.
        </li>
        <li>
          <strong>Polymers contained at the point of use:</strong> If microplastics are contained
          within a product in a way that prevents release into the environment (for example,
          encapsulated in a matrix that does not break down), they may be exempt. This creates a
          gray area that some manufacturers could exploit.
        </li>
        <li>
          <strong>Medical devices and in-vitro diagnostics:</strong> Products regulated under the
          EU Medical Devices Regulation have specific exemptions, recognizing that some medical
          applications have no viable alternatives to synthetic polymer particles.
        </li>
        <li>
          <strong>Products used at industrial sites:</strong> Microplastics used in industrial
          settings where release is controlled and emissions are managed under existing environmental
          permits have modified requirements rather than outright bans.
        </li>
        <li>
          <strong>Reporting-only obligations:</strong> For some product categories, the regulation
          requires only reporting and labeling rather than a full ban, at least during the transition
          period. This means products may still contain microplastics but must disclose it.
        </li>
      </ul>
      <p>
        These exceptions mean that even after full implementation, not all intentionally added
        microplastics will disappear from the market. Consumers who want to minimize exposure
        will still need to actively check products, which is why tools like ingredient scanning
        apps remain essential even in a more regulated market.
      </p>

      {/* Impact on Cosmetics Industry */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Impact on the Cosmetics Industry
      </h2>
      <p>
        The cosmetics and personal care industry is the sector most affected by the EU
        ban, and the ripple effects extend far beyond Europe. Because the EU is one of the
        world&apos;s largest consumer markets, global cosmetics companies are reformulating
        products for EU compliance -- and in many cases, rolling those reformulations out
        worldwide rather than maintaining separate product lines for different regions.
      </p>
      <p>
        The challenge is significant. Synthetic polymers in cosmetics are not just fillers. They
        serve critical functions: film-forming agents create a smooth, even finish in foundations;
        viscosity modifiers control the texture and spreadability of creams; opacifiers give
        products their characteristic appearance; and binding agents hold pressed powders together.
        Replacing these with natural alternatives that perform equally well, remain stable over
        shelf life, and meet consumer expectations for texture and appearance is a multimillion-dollar
        R&D challenge. For a comprehensive list of the specific ingredients being replaced, see our
        guide on the{' '}
        <Link href="/blog/worst-microplastic-ingredients" className="text-[#1E6B47] hover:underline font-medium">
          worst microplastic ingredients to watch for
        </Link>.
      </p>
      <p>
        Some brands have embraced the transition proactively, marketing their reformulated products
        as &quot;microplastic-free&quot; and turning compliance into a competitive advantage. Others
        have been slower to adapt, waiting until the deadlines force their hand. For consumers, this
        creates a temporarily uneven landscape where identical product categories may contain very
        different formulations depending on the brand and when it was manufactured.
      </p>

      {/* EU vs US Regulation */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        EU Ban vs. US Regulation: A Stark Contrast
      </h2>
      <p>
        The difference between European and American regulation of microplastics is dramatic. The
        United States passed the Microbead-Free Waters Act in 2015, which was considered landmark
        legislation at the time. However, that law is extremely narrow in scope: it bans only
        plastic microbeads in rinse-off cosmetics -- products like exfoliating face washes, body
        scrubs, and toothpaste that are rinsed down the drain after use.
      </p>
      <p>
        The US law does not cover:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Leave-on cosmetics (foundations, primers, moisturizers, sunscreens, lip products)</li>
        <li>Synthetic polymers that are not in microbead form (dissolved polymers, film-forming agents, copolymers)</li>
        <li>Detergents, cleaning products, or laundry supplies</li>
        <li>Artificial turf or sports pitch infill</li>
        <li>Agricultural coatings or controlled-release products</li>
        <li>Industrial applications of intentionally added microplastics</li>
        <li>Glitter or decorative microplastics</li>
      </ul>
      <p>
        In practical terms, this means that an American consumer buying a foundation, sunscreen,
        or laundry detergent has no federal protection against intentionally added microplastics
        in those products. A European consumer buying the same products will, by 2029, be protected
        by a comprehensive ban. The gap is enormous, and it is one of the reasons why American
        consumers need to be especially proactive about checking their products.
      </p>
      <p>
        Some US states have introduced their own microplastics legislation. California, for
        example, has taken steps toward requiring microplastic testing in drinking water and has
        explored broader product restrictions. But there is no federal equivalent to the EU&apos;s
        comprehensive REACH restriction, and the political landscape makes near-term federal
        action unlikely.
      </p>

      {/* What This Means for Consumers */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What This Means for Your Shopping
      </h2>
      <p>
        Whether you live in the EU or elsewhere, the regulation has practical implications for
        how you shop:
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>EU-compliant products are generally safer.</strong> If a product is sold in the
          EU market and complies with the new regulation, it will contain fewer or no intentionally
          added microplastics. Look for products manufactured for EU distribution as a proxy for
          higher safety standards.
        </li>
        <li>
          <strong>Product reformulations are happening now.</strong> Brands are actively changing
          their formulas. If you notice that a product you have been using for years suddenly looks,
          feels, or performs differently, the EU ban may be the reason. These changes are almost
          always for the better from a health perspective.
        </li>
        <li>
          <strong>Labels are becoming more transparent.</strong> The regulation&apos;s labeling
          and reporting requirements mean that microplastic content is increasingly disclosed.
          Check product packaging for new compliance marks or ingredient disclosures.
        </li>
        <li>
          <strong>Not everything is covered yet.</strong> Even in the EU, products in transition
          categories (leave-on cosmetics, detergents, agricultural products) may still contain
          microplastics until their specific deadline passes. Do not assume compliance just because
          a product is on an EU shelf.
        </li>
        <li>
          <strong>Outside the EU, you are largely on your own.</strong> In the US, Canada, and most
          other markets, there is no equivalent regulation. You need to check products yourself
          using ingredient labels, certifications, or scanning tools.
        </li>
      </ul>

      {/* How the MicroPlastics App Helps */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How the MicroPlastics App Helps You Navigate the Ban
      </h2>
      <p>
        The EU ban is a major step forward, but navigating the patchwork of transition periods,
        exceptions, and regional differences is not easy for the average consumer. This is exactly
        where the MicroPlastics app adds value. The app&apos;s product database is continuously
        updated to reflect regulatory changes, reformulations, and new ingredient data. When you
        scan a product, the app factors in:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Whether the product contains intentionally added microplastics covered by the EU ban</li>
        <li>Whether the product&apos;s category has reached its compliance deadline</li>
        <li>Whether the manufacturer has already reformulated ahead of the deadline</li>
        <li>The presence of synthetic polymers that fall outside the ban&apos;s scope but still pose exposure risks</li>
        <li>Packaging-related microplastic contamination that no regulation currently addresses</li>
      </ul>
      <p>
        This means the app gives you a more complete picture than the regulation alone provides.
        Even a product that is fully compliant with the EU ban may still receive a lower safety
        score if it contains non-regulated microplastic sources like certain packaging materials
        or polymers that fall outside the ban&apos;s technical definitions.
      </p>
      <p>
        For consumers outside the EU who do not benefit from the regulation at all, the app is
        even more essential. It applies the same rigorous analysis to every product regardless of
        where it is sold, effectively giving you EU-level scrutiny on products in markets with
        little or no microplastic regulation.
      </p>

      {/* What Is Still NOT Covered */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the Ban Still Does NOT Cover
      </h2>
      <p>
        It is important to understand the limits of even this landmark regulation. The EU
        microplastics ban, for all its ambition, does not address several major sources of
        microplastic exposure:
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Secondary microplastics from degradation:</strong> Microplastics generated when
          larger plastic items break down -- plastic bottles, packaging, bags, and containers -- are
          not covered. These &quot;secondary&quot; microplastics likely represent a larger source
          of environmental and human exposure than intentionally added particles.
        </li>
        <li>
          <strong>Tire wear particles:</strong> Tires are one of the single largest sources of
          microplastic pollution globally, shedding particles with every kilometer driven. The EU
          ban does not address tire wear, which requires entirely different regulatory approaches
          related to tire composition and road surface design.
        </li>
        <li>
          <strong>Textile fiber shedding:</strong> Synthetic clothing (polyester, nylon, acrylic)
          releases microfibers with every wash cycle. An estimated 500,000 tonnes of microfibers
          enter the ocean every year from laundry alone. The EU has separate initiatives exploring
          textile fiber pollution, but the microplastics ban does not cover it.
        </li>
        <li>
          <strong>Microplastics in food from packaging:</strong> Plastic food containers, bags,
          wraps, and bottles leach microplastics into food and beverages, especially when heated.
          The ban on intentionally added microplastics does not address this packaging-to-food
          contamination pathway.
        </li>
        <li>
          <strong>Microplastics in water supplies:</strong> Drinking water, both bottled and tap,
          contains measurable levels of microplastics. While the EU has separate water quality
          directives, the microplastics ban itself does not address waterborne exposure.
        </li>
        <li>
          <strong>Paint and coating wear:</strong> Paints on buildings, ships, and infrastructure
          shed microplastic particles as they weather and degrade. While some paint formulations
          fall under the ban for intentionally added particles, the wear and degradation pathway
          is not covered.
        </li>
      </ul>
      <p>
        These gaps mean that even in a post-ban world, microplastic exposure will remain a
        significant concern. The regulation addresses one important piece of the puzzle --
        intentional addition -- but the broader challenge of plastic pollution requires systemic
        changes to how we manufacture, use, and dispose of plastic materials at every level.
      </p>

      {/* Conclusion */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Bottom Line: Progress, but Not a Complete Solution
      </h2>
      <p>
        The EU microplastics ban is genuinely historic. It is the first regulation in the world to
        comprehensively restrict intentionally added microplastics across consumer, industrial, and
        agricultural products. It will prevent an estimated 500,000 tonnes of microplastics from
        entering the environment over 20 years. It is forcing an entire industry to innovate and
        find alternatives to synthetic polymers that have been used for decades.
      </p>
      <p>
        But it is not a silver bullet. The phase-in timeline means full compliance is still years
        away. The exceptions and loopholes create gray areas. And the enormous categories of
        secondary microplastics, tire wear, textile shedding, and packaging contamination remain
        entirely outside its scope. For consumers, the takeaway is clear: regulation is moving in
        the right direction, but personal vigilance is still essential.
      </p>
      <p>
        Check your products. Read your labels. Understand what the ban covers and what it does
        not. And use every tool available to you -- including the MicroPlastics app -- to make
        informed decisions about what you put on your skin, in your body, and in your home.
        Regulation helps, but your own choices remain the most powerful line of defense.
      </p>
      <p className="font-semibold text-[#1E6B47]">
        Download the MicroPlastics app today and scan your products. See which ones are already
        compliant with the EU ban -- and which ones are not.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Product packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from photo, scratches, dents, fade.</>,
          <>Brand and product category, different SKUs in the same brand can score differently.</>,
          <>Use-context flags you log, microwave, heat, reuse.</>,
          <>Cited published research behind each 0–100 score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Get the MicroPlastics app"
        sub="Scan barcodes + packaging photos. Get a 0–100 microplastic risk score with linked research and safer alternatives. Free to start on iOS."
        buttonLabel="Get the MicroPlastics app"
      />


      <BlogFAQ
        items={[
          {
            question: 'When did the EU ban microplastics?',
            answer:
              'The EU\'s microplastics restriction (Regulation EU 2023/2055) entered force on October 17, 2023. It bans intentionally added microplastic particles in a wide range of consumer and industrial products, with category-specific phase-out deadlines extending through 2035.',
          },
          {
            question: 'What products are affected by the EU microplastics ban?',
            answer:
              'Loose glitter (banned immediately), rinse-off cosmetic microbeads, detergent capsules, fragrance encapsulates, leave-on cosmetics (sunscreen, moisturizer, serum), colour cosmetics (lipstick, foundation, nail polish), synthetic sports-pitch infill (artificial turf), and agricultural seed coatings. Each category has a different compliance deadline between 2023 and 2035 — rinse-off by October 2027, leave-on by October 2029, and lip, nail and make-up products by October 2035.',
          },
          {
            question: 'Does the EU microplastics ban apply to all microplastics?',
            answer:
              'No. It applies only to intentionally added microplastic particles, those deliberately added as ingredients. It does not cover microplastics that result from the breakdown of larger plastic items or from packaging migration, which are governed by separate regulations.',
          },
          {
            question: 'Are US products affected by the EU microplastics ban?',
            answer:
              'Only if they are sold in the EU. US-domestic products are not affected by EU regulations. The US has no equivalent federal restriction. California, New York, and a few other states have partial bans on microbeads in rinse-off cosmetics.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'European Commission',
            year: 2026,
            title: 'Commission Regulation (EU) 2026/1168 amending REACH Annex XVII, Entry 78 (synthetic polymer microparticles)',
            publisher: 'European Commission',
            url: 'https://en.reach24h.com/news/industry-news/chemical/reach-microplastics-regulation-2026-1168',
          },
          {
            authors: 'Qian N, Gao X, Lang X, et al.',
            year: 2024,
            title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy',
            publisher: 'PNAS',
            url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121',
          },
          {
            authors: 'Marfella R, Prattichizzo F, Sardu C, et al.',
            year: 2024,
            title: 'Microplastics and Nanoplastics in Atheromas and Cardiovascular Events',
            publisher: 'New England Journal of Medicine',
            url: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2309822',
          },
          {
            authors: 'Garcia MA, Liu R, Nihart A, et al.',
            year: 2024,
            title: 'Quantitation and identification of microplastics in human placental specimens',
            publisher: 'Toxicological Sciences',
            url: 'https://academic.oup.com/toxsci/article/199/1/81/7609299',
          },
          {
            authors: 'Leslie HA, van Velzen MJM, Brandsma SH, et al.',
            year: 2022,
            title: 'Discovery and quantification of plastic particle pollution in human blood',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412022001258',
          },
          {
            authors: 'WHO',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles',
            publisher: 'World Health Organization',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
